import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';

import { devApi, prodDevApi } from '../../../api/axios';
import { IApiResponseError, IApiState } from '../../../types/api';
import { IWalletTransactionsTable } from '../../../types/betting';
import { RootState } from '../..';

interface IGetWalletTransactionsRequest {
  page?: number;
  pageSize?: number;
  startDate?: string;
  endDate?: string;
}

interface IGetWalletTransactionsResponse {
  totalSuccessCount?: number;
  totalSuccessSum?: number;
  totalFailedCount?: number;
  totalFailedSum?: number;
  totalSum?: number;
  totalCount?: number;
  status?: boolean;
  message?: string;
  data?: IWalletTransactionsTable[];
  errors?: IApiResponseError[];
}

const initialState: IApiState<IGetWalletTransactionsResponse> = {
  value: null,
  status: 'idle',
  error: '',
};

export const getWalletTransactionsAsync = createAsyncThunk(
  'walletTransactions/get',
  async (
    {
      page = 1,
      pageSize = 10,
      startDate,
      endDate,
    }: IGetWalletTransactionsRequest & { startDate?: string; endDate?: string },
    { rejectWithValue, getState }
  ) => {
    try {
      const state = getState() as RootState;

      const accessToken = state.auth.value?.data?.token?.accessToken;

      const devTokenInStorage = localStorage.getItem('devTokenData');

      const parsedDevTokenInStorage = devTokenInStorage
        ? JSON.parse(devTokenInStorage)
        : null;

      const developerId = localStorage.getItem('developerId');

      const environment = state.environment?.value;

      const apiInstance = environment === 'live' ? prodDevApi : devApi;

      const authToken =
        environment === 'live' ? accessToken : parsedDevTokenInStorage;

      const config = {
        headers: {
          Authorization: `Token ${authToken}`,
          'Business-id': developerId,
          Environment: 'live',
        },
      };

      let url = `/betting/wallet-deposits?page-size=${pageSize}&page-number=${page}`;

      if (startDate && endDate) {
        url += `&start-date=${startDate}&end-date=${endDate}`;
      }

      const { data } = await apiInstance.get(url, config);
      return data;
    } catch (err) {
      const error = err as AxiosError<IApiResponseError>;

      return rejectWithValue(error.response?.data);
    }
  }
);

export const walletTransactionsGetSlice = createSlice({
  name: 'walletTransactions/get',
  initialState,
  reducers: {
    resetGetWalletTransactions: (state) => {
      state.value = null;
      state.status = 'idle';
      state.error = '';
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getWalletTransactionsAsync.pending, (state) => {
        state.status = 'loading';
        state.error = '';
      })
      .addCase(getWalletTransactionsAsync.fulfilled, (state, action) => {
        state.status = 'success';
        state.value = action.payload as IGetWalletTransactionsResponse;
      })
      .addCase(getWalletTransactionsAsync.rejected, (state, action) => {
        state.status = 'failed';
        state.value = action.payload as IGetWalletTransactionsResponse;
        state.error = action.error.message;
      });
  },
});

export const { resetGetWalletTransactions } =
  walletTransactionsGetSlice.actions;

export default walletTransactionsGetSlice.reducer;
